import { ColumnState } from '@ag-grid-community/core';
import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { AUTH_SERVER_URL } from '../../config';
import { State } from '../../order/models/localization-state';
import { getDefaultDatacenterViews } from '../datacenter/default-datacenter-views';
import { ColDefWithFields } from '../datacenter/column-type';
import { DatacenterType, UserDatacenterView, UserValueSelected } from '../models/datacenter-aggrid-view';

import { TranslationService } from './translation.service';
import { UserService } from './user.service';

const AUTH_UPDATE_USER_VIEW = `${ AUTH_SERVER_URL }/user/userDatacenterViews:save`;
const AUTH_REMOVE_USER_VIEW = `${ AUTH_SERVER_URL }/user/userDatacenterViews:delete`;
const AUTH_EXTERNAL_USERS = `${ AUTH_SERVER_URL }/user/users:external`;
const AUTH_USERS_SHARE_VIEW = `${ AUTH_SERVER_URL }/user/userDatacenterViews:external/share`;

interface IDatacenterViewService {
	convertViewToColumnDefinitions(
		view: UserDatacenterView,
		allColumns: ColDefWithFields[]
	): (ColDefWithFields & ColumnState)[];
	getCurrentView(defaultView: UserDatacenterView): UserDatacenterView;
	getViewList(datacenterType: DatacenterType): UserDatacenterView[];
	saveCurrentView(view: UserDatacenterView): Promise<void>;
}

@Injectable({ providedIn: 'root' })
export class DatacenterViewService implements IDatacenterViewService {
	private readonly viewVersion = 1;
	private _viewToEdit: {
		currentView: UserDatacenterView;
		viewType: DatacenterType;
	} | undefined;

	private readonly translationService = inject(TranslationService);

	constructor(
		private readonly location: Location,
		private readonly http: HttpClient,
		private readonly router: Router,
		private readonly userService: UserService,
	) {}

	get viewToEdit() {
		return this._viewToEdit;
	}

	set viewToEdit(
		viewToEdit: {
			currentView: UserDatacenterView;
			viewType: DatacenterType;
		} | undefined
	) {
		this._viewToEdit = viewToEdit;
	}

	saveUserView(userView: Omit<UserDatacenterView, 'id'> & Pick<Partial<UserDatacenterView>, 'id'>) {
		// Reject overwriting built-in default views (negative IDs are sentinels)
		if (userView.id !== undefined && this.isBuiltInView(userView.id)) {
			throw new Error('Cannot overwrite built-in default view');
		}

		if (!navigator.onLine) {
			throw new Error('not online');
		}
		// Filter out columns with groupId before saving
		const columnStateWithColIDOnly = userView.columnState.filter(col => !col.groupId);
		const tempView = {
			...userView,
			columnState: JSON.stringify(columnStateWithColIDOnly),
			filterState: userView.filterState === null ? userView.filterState : JSON.stringify(userView.filterState),
			sortModel: userView.sortModel === null ? userView.sortModel : JSON.stringify(userView.sortModel)
		};
		tempView.version = this.viewVersion;
		return this.http.post(AUTH_UPDATE_USER_VIEW, tempView, { responseType: 'text' });
	}

	shareUserView(
		userView: Omit<UserDatacenterView, 'id'> & Pick<Partial<UserDatacenterView>, 'id'>,
		personIds: number[]
	) {
		// Reject sharing built-in default views (negative IDs are sentinels)
		if (userView.id !== undefined && this.isBuiltInView(userView.id)) {
			throw new Error('Cannot share built-in default view');
		}

		if (!navigator.onLine) {
			throw new Error('not online');
		}
		// Filter out columns with groupId before saving
		const columnStateWithColIDOnly = userView.columnState.filter(col => !col.groupId);
		const tempView = {
			...userView,
			columnState: JSON.stringify(columnStateWithColIDOnly),
			filterState: userView.filterState === null ? userView.filterState : JSON.stringify(userView.filterState),
			sortModel: userView.sortModel === null ? userView.sortModel : JSON.stringify(userView.sortModel)
		};
		tempView.version = this.viewVersion;
		const viewToShare = {
			userDatacenterViewResponse: tempView,
			personIds
		};
		return this.http.post(AUTH_USERS_SHARE_VIEW, viewToShare, { responseType: 'json' });
	}

	async updateUserData() {
		await this.userService.updateUserData();
	}

	async removeUserView(viewId: number) {
		// Reject removal of built-in default views (negative IDs are sentinels)
		if (this.isBuiltInView(viewId)) {
			throw new Error('Cannot delete built-in default view');
		}

		if (!navigator.onLine) {
			return;
		}
		const url = new URL(AUTH_REMOVE_USER_VIEW);
		const params = url.searchParams;
		params.append('view_id', String(viewId));

		await firstValueFrom(this.http.post(url.toString(), {}));
		await this.userService.updateUserData();
	}

	getViewList(datacenterType: DatacenterType) {
		const defaults = getDefaultDatacenterViews(this.userService.user, datacenterType, {
			invoice: this.translationService.getTR('commercial-documents.invoice'),
			dispatchAdvice: this.translationService.getTR('datacenter-filter.dispatch-advice'),
			orderAcknowledgment: this.translationService.getTR('order.card.document.order.acknowledgments'),
			certificate: this.translationService.getTR('order.card.document.certificates'),
			xcarb: this.translationService.getTR('order.card.xcarb')
		});
		const userViews = this.userService.user.datacenterViews
			.filter(value => value.type === datacenterType);
		return [...defaults, ...userViews];
	}

	getCurrentView(defaultView: UserDatacenterView) {
		const state: State = <State> this.location.getState();
		const view: UserDatacenterView = state.view ?? defaultView;
		const clone: UserDatacenterView = {
			...view,
			columnState: [...view.columnState],
			filterState: view.filterState ? [...view.filterState] : view.filterState,
			sortModel: view.sortModel ? [...view.sortModel] : view.sortModel
		};
		this.applyState(clone, state);
		return clone;
	}

	async saveCurrentView(view: UserDatacenterView) {
		await this.router.navigateByUrl(
			this.router.url,
			{
				replaceUrl: true,
				state: {
					...<State> this.location.getState(),
					view
				}
			}
		);
	}

	getUserBySearchText(query: string, serviceCode: string) {
		return this.sendUserRequest(query, serviceCode);
	}

	convertViewToColumnDefinitions(
		view: UserDatacenterView,
		allColumns: ColDefWithFields[]
	): (ColDefWithFields & ColumnState)[] {
		const { columnState, sortModel } = view;
		if (columnState.length === 0) {
			throw new Error('No state columns');
		}
		const columnsThatAreGroups = new Set<string>();
		for (const col of allColumns) {
			if (col.groupId) {
				columnsThatAreGroups.add(col.groupId);
			}
		}
		// group columns by groupID, if column has no group, it will not be present in the map
		const columnsByGroupID = this.groupColumnByGroupID(allColumns);
		// Expand the view columns (groups → all columns of the group)
		const expandedColumnState: typeof columnState = [];
		const processedColIds = new Set<string>();

		for (const state of columnState) {
			const { colId } = state;
			// If the colId is a groupId, add all columns of the group
			if (columnsThatAreGroups.has(colId)) {
				const groupColumns = columnsByGroupID.get(colId);
				if (groupColumns) {
					for (const groupCol of groupColumns) {
						if (groupCol.colId && !processedColIds.has(groupCol.colId)) {
							expandedColumnState.push({ ...state, colId: groupCol.colId });
							processedColIds.add(groupCol.colId);
						}
					}
				}
			} else if (!processedColIds.has(colId)) {
				expandedColumnState.push(state);
				processedColIds.add(colId);
			}
		}
		const colIdStateIndex: Record<string, number | undefined> = {};
		function getAndCacheStateIndex(colId: string) {
			const cacheValue = colIdStateIndex[colId];
			if (cacheValue !== undefined) {
				return cacheValue;
			}
			const index = expandedColumnState.findIndex(state => state.colId === colId);
			if (index !== -1) {
				colIdStateIndex[colId] = index;
				return index;
			}
			return undefined;
		}
		function getSort(colId: string) {
			return sortModel?.find(model => model.colId === colId)?.sort ?? null;
		}
		function getSortIndex(colId: string) {
			const index = sortModel?.findIndex(model => model.colId === colId);
			return index !== undefined && index > -1 ? index : null;
		}
		/*	Keep all column but only mandatory or in state columns are visible
			Hidden columns are required when filtering or sorting on those columns
			Unexisting columns in state are also ignored
		*/
		return allColumns.reduce((result: (ColDefWithFields & ColumnState)[], column: ColDefWithFields) => {
			if (column.colId) {
				const stateIndex = getAndCacheStateIndex(column.colId);
				result.push({
					...column,
					...stateIndex !== undefined ? expandedColumnState[stateIndex] : {},
					colId: column.colId,
					hide: !column.isMandatoryOnLeft && stateIndex === undefined,
					sort: getSort(column.colId),
					sortIndex: getSortIndex(column.colId),
				});
			}
			return result;
		}, [] /* initial value */).sort((column1, column2) => {
			if (column1.hide && column2.hide) {
				return 0;
			} else if (!column1.hide && column2.hide) {
				return -1;
			} else if (column1.hide && !column2.hide) {
				return 1;
			}
			if (column1.isMandatoryOnLeft && column2.isMandatoryOnLeft) {
				return 0;
			} else if (column1.isMandatoryOnLeft && !column2.isMandatoryOnLeft) {
				return -1;
			} else if (!column1.isMandatoryOnLeft && column2.isMandatoryOnLeft) {
				return 1;
			}
			const index1 = getAndCacheStateIndex(column1.colId);
			const index2 = getAndCacheStateIndex(column2.colId);
			if (index1 === undefined && index2 === undefined) {
				return 0;
			} else if (index2 === undefined) {
				return -1;
			} else if (index1 === undefined) {
				return 1;
			}
			const state1 = expandedColumnState[index1];
			const state2 = expandedColumnState[index2];
			if (state1.pinned === 'left' && state2.pinned !== 'left') {
				return -1;
			} else if (state1.pinned !== 'left' && state2.pinned === 'left') {
				return 1;
			}
			return index1 - index2;
		});
	}

	private groupColumnByGroupID(allColumns: ColDefWithFields[]): Map<string, ColDefWithFields[]> {
		const groups = new Map<string, ColDefWithFields[]>();

		for (const col of allColumns) {
			if (col.groupId && col.colId && col.colId !== col.groupId) {
				if (!groups.has(col.groupId)) {
					groups.set(col.groupId, []);
				}
				groups.get(col.groupId)?.push(col);
			}
		}
		return groups;
	}

	private applyState(view: UserDatacenterView, state: State) {
		if (!state.filter?.length && !state.sort?.length) {
			return;
		}
		if (state.filter) {
			view.filterState = state.filter;
		}
		if (state.sort) {
			view.sortModel = state.sort;
		}
		if (state.columnId) {
			const isColumnFound = view.columnState.some(column => column.colId === state.columnId);
			if (!isColumnFound) {
				view.columnState.push({ colId: state.columnId, pinned: 'left' });
			}
		}
	}

	/*
	private safeColumn(colId: string, columns: CommonColDef[]) {
		const column = columns.find(col => col.colId === colId);
		if (!column) {
			throw new Error('Unknwon state column');
		}
		return column;
	}
	*/

	private sendUserRequest(searchText: string, serviceCode: string) {
		const url = new URL(AUTH_EXTERNAL_USERS);
		const params = url.searchParams;

		if (searchText) {
			params.append('userName', searchText);
		}
		params.append('serviceCode', serviceCode);

		return this.http.get<UserValueSelected[]>(url.toString());
	}

	private isBuiltInView(viewId: number): boolean {
		return viewId < 0; // Sentinel IDs are negative
	}
}
